import { Box, Flex, Text } from "rebass";
import styled from "styled-components";
import type { ReactNode } from "react";

import { StudioIcon } from "MVVM/Views/Studio/Components/StudioIcons";
import { EStudioIconType } from "MVVM/Views/Studio/@types";

interface IGame {
  key: string;
  name: string;
  icon: ReactNode;
}

interface IGamesPillSelectorProps {
  selectedGames: string[];
  onGameToggle: (gameKey: string) => void;
}

const AVAILABLE_GAMES: IGame[] = [
  {
    key: "CS2",
    name: "Counter-Strike 2",
    icon: <StudioIcon size={20} type={EStudioIconType.CS2} />,
  },
  {
    key: "FORTNITE",
    name: "Fortnite",
    icon: <StudioIcon size={20} type={EStudioIconType.Fortnite} />,
  },
  {
    key: "LOL",
    name: "League of Legends",
    icon: <StudioIcon size={20} type={EStudioIconType.LoL} />,
  },
  {
    key: "DOTA2",
    name: "Dota 2",
    icon: <StudioIcon size={20} type={EStudioIconType.Dota2} />,
  },
  {
    key: "OW2",
    name: "Overwatch 2",
    icon: <StudioIcon size={20} type={EStudioIconType.OW2} />,
  },
];

export const GamesPillSelector = ({
  selectedGames,
  onGameToggle,
}: IGamesPillSelectorProps) => {
  const sortedGames = AVAILABLE_GAMES.map((game) => {
    const isPlayed = selectedGames.includes(game.key);
    return { ...game, isPlayed };
  }).sort((a, b) => (b.isPlayed ? 1 : 0) - (a.isPlayed ? 1 : 0));

  return (
    <GamesList>
      {sortedGames.map((game) => (
        <GamePill
          key={game.key}
          $isHighlighted={game.isPlayed}
          onClick={() => onGameToggle(game.key)}
        >
          <IconWrapper className="icon" $isHighlighted={game.isPlayed}>
            {game.icon}
          </IconWrapper>
          <GameText $isHighlighted={game.isPlayed}>{game.name}</GameText>
        </GamePill>
      ))}
    </GamesList>
  );
};

const GamesList = styled(Flex)`
  flex-wrap: wrap;
  gap: 8px;
`;

const GamePill = styled(Flex)<{ $isHighlighted: boolean }>`
  align-items: center;
  background: ${({ $isHighlighted, theme }) =>
    $isHighlighted ? theme.colors.navy50 : "transparent"};
  border: 1px solid
    ${({ theme, $isHighlighted }) =>
      $isHighlighted ? theme.colors.envy : theme.colors.navy50};
  border-radius: 999px;
  cursor: pointer;
  font-family: Barlow;
  font-weight: 500;
  gap: 8px;
  opacity: 1;
  padding: 8px 12px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $isHighlighted, theme }) =>
      $isHighlighted ? theme.colors.navy50 : "rgba(37, 58, 73, 0.3)"};
  }
`;

const IconWrapper = styled(Box)<{ $isHighlighted?: boolean }>`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  filter: ${({ $isHighlighted }) =>
    $isHighlighted ? "none" : "grayscale(100%) opacity(0.5)"};
  justify-content: center;
  transition: filter 0.2s ease;
`;

const GameText = styled(Text)<{ $isHighlighted: boolean }>`
  color: ${({ theme, $isHighlighted }) =>
    $isHighlighted ? theme.colors.ice : theme.colors.shade300};
  font-family: Barlow;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
`;
